<template>
    <PageHeader title="Dashboard" :subtitle="summaryText" />

    <div class="data-card">
        <div class="data-filters">
            <div class="data-filters-main">
                <div class="filter-field filter-period">
                    <label>Período</label>
                    <div class="filter-period-inputs">
                        <CalendarForm pickerRange v-model="stateCalendarFilter.datesInterval" :maxDate="new Date()"
                            placeholder="Intervalo de datas" />
                    </div>
                </div>

                <div class="data-filters-actions">
                    <Button class="p-button-outlined" type="button"
                        :icon="state.showFilters ? 'pi pi-chevron-up' : 'pi pi-sliders-h'"
                        :label="activeFilters.length ? `Filtros (${activeFilters.length})` : 'Filtros'"
                        @click="state.showFilters = !state.showFilters" />
                    <Button type="button" icon="pi pi-search" label="Pesquisar" :loading="state.loading.summary"
                        @click="loadSummary" />
                </div>
            </div>

            <div v-show="state.showFilters" class="data-filters-grid">
                <div class="filter-field">
                    <label>Cliente</label>
                    <MultiSelect v-model="state.filter.clients" optionLabel="name" dataKey="id" filter
                        :options="stateClients.clients" :loading="stateClients.loading.clients"
                        :maxSelectedLabels="2" selectedItemsLabel="{0} clientes" placeholder="Todos" />
                </div>

                <div class="filter-field">
                    <label>Status</label>
                    <Dropdown showClear v-model="state.filter.status" optionLabel="name" :options="statusOptions"
                        placeholder="Todos" />
                </div>

                <div class="filter-field">
                    <label>Tipo</label>
                    <Dropdown showClear v-model="state.filter.type" optionLabel="name" :options="summaryTypesOptions"
                        placeholder="Todos" />
                </div>

                <div class="filter-field">
                    <label>Fornecedor</label>
                    <MultiSelect v-model="state.filter.vendors" optionLabel="name" dataKey="id" filter
                        :options="stateVendors.vendors" :loading="stateVendors.loading.vendors"
                        :maxSelectedLabels="2" selectedItemsLabel="{0} fornecedores" placeholder="Todos" />
                </div>
            </div>

            <div v-if="activeFilters.length" class="data-filters-chips">
                <span v-for="chip in activeFilters" :key="chip.key" class="filter-chip">
                    <span class="filter-chip-label">{{ chip.label }}:</span> {{ chip.value }}
                    <button type="button" class="filter-chip-clear" :aria-label="`Remover filtro ${chip.label}`"
                        @click="clearFilter(chip.key)">
                        <i class="pi pi-times"></i>
                    </button>
                </span>
                <Button class="p-button-text p-button-sm" type="button" icon="pi pi-filter-slash" label="Limpar filtros"
                    @click="clearAllFilters" />
            </div>
        </div>
    </div>

    <div v-if="state.loading.summary" class="data-card data-form-loading">
        <ProgressSpinner />
    </div>

    <template v-else>

        <div class="summary-cards">
            <SummaryCard v-for="card in summaryCards" :key="card.key" :title="card.title" :icon="card.icon"
                :accentColor="card.accentColor" :amount="card.amount" :count="card.count" :totalTax="card.totalTax"
                :calculatedAmount="card.calculatedAmount" :share="card.share" :highlight="card.highlight" />
        </div>

        <div class="summary-panels">

            <div class="data-card">
                <div class="data-card-toolbar">
                    <div class="summary-panel-heading">
                        <span class="record-name">Volume por {{ chartGranularityLabel }}</span>
                        <span class="data-page-subtitle">Depósitos e saques do período filtrado</span>
                    </div>
                </div>

                <div class="summary-chart">
                    <Chart v-if="hasChartData" type="line" :data="chartData" :options="chartOptions" />
                    <div v-else class="data-empty">
                        <i class="pi pi-chart-line"></i>
                        <span>{{ chartEmptyMessage }}</span>
                    </div>
                </div>
            </div>

            <div class="data-card">
                <div class="data-card-toolbar">
                    <div class="summary-panel-heading">
                        <span class="record-name">Resumo do período</span>
                        <span class="data-page-subtitle">{{ periodLabel || 'Todo o período' }} · status {{ statusLabelText }}</span>
                    </div>
                </div>

                <div class="summary-panel-body">
                    <div class="summary-metrics">
                        <div v-for="metric in summaryMetrics" :key="metric.label" class="summary-metric-row">
                            <span class="summary-metric-label">{{ metric.label }}</span>
                            <span class="summary-metric-value" :class="metric.textColor">{{ metric.value }}</span>
                        </div>
                    </div>

                    <div class="summary-effective-rate">
                        <span class="summary-effective-rate-label">Taxa efetiva sobre o volume</span>
                        <span class="summary-effective-rate-value">{{ formatPercent(summaryTotals.effectiveRate) }}</span>
                    </div>
                </div>
            </div>

        </div>

        <div class="data-card">
            <div class="data-card-toolbar">
                <div class="summary-panel-heading">
                    <span class="record-name">Movimentação por fornecedor</span>
                    <span class="data-page-subtitle">
                        {{ periodLabel || 'Todo o período' }} · expanda um fornecedor para ver os clientes
                    </span>
                </div>
            </div>

            <DataTable class="data-table vendor-summary-table" :value="vendorRows"
                v-model:expandedRows="state.expandedVendors" dataKey="rowKey" responsiveLayout="scroll"
                sortField="grossAmount" :sortOrder="-1">

                <template #empty>
                    <div class="data-empty">
                        <i class="pi pi-inbox"></i>
                        <span>{{ vendorEmptyMessage }}</span>
                    </div>
                </template>

                <Column :expander="true" headerStyle="width: 3rem" />

                <Column field="vendorName" header="Fornecedor" sortable :footer="'TOTAL'">
                    <template #body="slotProps">
                        <div class="vendor-name-cell">
                            <span class="pill pill-info">{{ slotProps.data.vendorName }}</span>
                            <span class="cell-subtext">{{ clientCountLabel(slotProps.data.clients.length) }}</span>
                        </div>
                    </template>
                </Column>

                <Column field="depositsAmount" header="Depósitos" sortable class="col-numeric"
                    :footer="formatCurrency(vendorTotals.depositsAmount)">
                    <template #body="slotProps">
                        <span class="cell-amount">{{ formatCurrency(slotProps.data.depositsAmount) }}</span>
                        <span class="cell-subtext">{{ countLabel(slotProps.data.depositsCount) }}</span>
                    </template>
                </Column>

                <Column field="withdrawAmount" header="Saques" sortable class="col-numeric"
                    :footer="formatCurrency(vendorTotals.withdrawAmount)">
                    <template #body="slotProps">
                        <span class="cell-amount">{{ formatCurrency(slotProps.data.withdrawAmount) }}</span>
                        <span class="cell-subtext">{{ countLabel(slotProps.data.withdrawCount) }}</span>
                    </template>
                </Column>

                <Column field="removalAmount" header="Retiradas" sortable class="col-numeric"
                    :footer="formatCurrency(vendorTotals.removalAmount)">
                    <template #body="slotProps">
                        <span class="cell-amount">{{ formatCurrency(slotProps.data.removalAmount) }}</span>
                        <span class="cell-subtext">{{ countLabel(slotProps.data.removalCount) }}</span>
                    </template>
                </Column>

                <Column field="totalTax" header="Taxas" sortable class="col-numeric"
                    :footer="formatCurrency(vendorTotals.totalTax)">
                    <template #body="slotProps">
                        <span class="summary-metric-tax">{{ formatCurrency(slotProps.data.totalTax) }}</span>
                    </template>
                </Column>

                <Column field="balance" header="Saldo" sortable class="col-numeric"
                    :footer="formatCurrency(vendorTotals.balance)">
                    <template #body="slotProps">
                        <span class="cell-amount" :class="slotProps.data.balance < 0 ? 'is-negative' : 'is-positive'">
                            {{ formatCurrency(slotProps.data.balance) }}
                        </span>
                    </template>
                </Column>

                <template #expansion="slotProps">
                    <div class="client-breakdown">
                        <div class="client-breakdown-heading">
                            <div>
                                <span class="record-name">Clientes de {{ slotProps.data.vendorName }}</span>
                                <span class="data-page-subtitle">
                                    Saldo = depósitos - saques - retiradas - taxas
                                </span>
                            </div>
                            <span class="pill pill-muted">{{ clientCountLabel(slotProps.data.clients.length) }}</span>
                        </div>

                        <DataTable class="data-table client-breakdown-table" :value="slotProps.data.clients"
                            responsiveLayout="scroll">
                            <template #empty>
                                <div class="data-empty client-breakdown-empty">
                                    <i class="pi pi-users"></i>
                                    <span>A API não retornou os clientes deste fornecedor.</span>
                                </div>
                            </template>

                            <Column field="clientName" header="Cliente">
                                <template #body="clientSlotProps">
                                    <span class="record-name">{{ clientSlotProps.data.clientName }}</span>
                                </template>
                            </Column>

                            <Column field="depositsAmount" header="Depósitos" class="col-numeric">
                                <template #body="clientSlotProps">
                                    <span class="cell-amount">{{ formatCurrency(clientSlotProps.data.depositsAmount) }}</span>
                                    <span class="cell-subtext">{{ countLabel(clientSlotProps.data.depositsCount) }}</span>
                                </template>
                            </Column>

                            <Column field="withdrawAmount" header="Saques" class="col-numeric">
                                <template #body="clientSlotProps">
                                    <span class="cell-amount">{{ formatCurrency(clientSlotProps.data.withdrawAmount) }}</span>
                                    <span class="cell-subtext">{{ countLabel(clientSlotProps.data.withdrawCount) }}</span>
                                </template>
                            </Column>

                            <Column field="removalAmount" header="Retiradas" class="col-numeric">
                                <template #body="clientSlotProps">
                                    <span class="cell-amount">{{ formatCurrency(clientSlotProps.data.removalAmount) }}</span>
                                    <span class="cell-subtext">{{ countLabel(clientSlotProps.data.removalCount) }}</span>
                                </template>
                            </Column>

                            <Column field="totalTax" header="Taxas" class="col-numeric">
                                <template #body="clientSlotProps">
                                    <span class="summary-metric-tax">{{ formatCurrency(clientSlotProps.data.totalTax) }}</span>
                                </template>
                            </Column>

                            <Column field="balance" header="Saldo" class="col-numeric">
                                <template #body="clientSlotProps">
                                    <span class="cell-amount"
                                        :class="clientSlotProps.data.balance < 0 ? 'is-negative' : 'is-positive'">
                                        {{ formatCurrency(clientSlotProps.data.balance) }}
                                    </span>
                                </template>
                            </Column>
                        </DataTable>
                    </div>
                </template>

            </DataTable>
        </div>

    </template>
</template>

<script>

import { computed, reactive } from 'vue'
import { useStore } from 'vuex'
import moment from 'moment'
import services from '../../services'
import ProgressSpinner from 'primevue/progressspinner'
import Chart from 'primevue/chart'
import Button from 'primevue/button'
import Dropdown from 'primevue/dropdown'
import MultiSelect from 'primevue/multiselect'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import useCalendarFilter from '../../composables/useCalendarFilter'
import useClients from '../../composables/useClients'
import useVendors from '../../composables/useVendors'
import useEnumLabels from '../../composables/useEnumLabels'
import useNotificationToast from '../../composables/useNotificationToast'
import CalendarForm from '../../components/CalendarForm.vue'
import PageHeader from '../../components/PageHeader.vue'
import SummaryCard from '../../components/SummaryCard.vue'

export default {
    components: {
        Button,
        Dropdown,
        MultiSelect,
        DataTable,
        Column,
        ProgressSpinner,
        Chart,
        CalendarForm,
        PageHeader,
        SummaryCard,
    },
    setup() {

        const state = reactive({
            filter: {
                clients: [],
                status: null,
                type: null,
                vendors: [],
            },
            showFilters: false,
            expandedVendors: [],
            loading: {
                summary: false,
            },
            summary: {
                depositsAmount: null,
                depositsCount: null,
                depositsTax: null,
                totalAmount: null,
                totalCount: null,
                withdrawAmount: null,
                withdrawCount: null,
                withdrawTax: null,
                removalAmount: null,
                removalCount: null,
                removalTax: null,
                timeline: null,
                vendors: null,
            },
        })

        // o gráfico é canvas: precisa de cor literal, não de custom property do tema
        const CHART_COLORS = {
            deposits: '#1c7a46',
            depositsFill: 'rgba(28, 122, 70, 0.14)',
            withdraws: '#b53434',
            withdrawsFill: 'rgba(181, 52, 52, 0.14)',
            removals: '#96620a',
            removalsFill: 'rgba(150, 98, 10, 0.12)',
            grid: 'rgba(16, 24, 40, 0.08)',
            text: '#6b7079',
        }

        const { statusOptions, typesOptions } = useEnumLabels()

        // o resumo só soma DEPOSIT, WITHDRAW e REMOVAL
        const summaryTypesOptions = typesOptions.filter((option) => option.value !== 'MANUAL')

        const { stateClients, loadClientsWithoutPagination } = useClients()
        const { stateVendors, loadVendorsWithoutPagination } = useVendors()
        const { stateCalendarFilter, filterToday } = useCalendarFilter(loadSummary)
        const { error } = useNotificationToast()
        const store = useStore()

        state.filter.status = statusOptions[1]
        filterToday()

        loadClientsWithoutPagination()
        loadVendorsWithoutPagination()

        function loadSummary() {

            state.loading.summary = true
            const token = store.getters.getToken

            const params = {
                filter: {
                    ...state.filter,
                    date: {
                        begin: stateCalendarFilter.date.begin,
                        end: stateCalendarFilter.date.end,
                    },
                }
            }

            services.transactions.getSummary({ params, token })
                .then((response) => {

                    state.summary = response.data
                    state.expandedVendors = []

                }).catch(() => {

                    error('Não foi possível carregar o resumo.')

                }).finally(() => {

                    state.loading.summary = false

                })
        }

        const periodLabel = computed(() => {
            const { begin, end } = stateCalendarFilter.date

            if (!begin && !end) return null

            const format = (date) => moment(date, 'YYYY/MM/DD').format('DD/MM/YYYY')

            if (begin && end && begin !== end) return `${format(begin)} a ${format(end)}`

            return format(begin || end)
        })

        // até dois itens o chip mostra os nomes; acima disso o nome de todos não caberia
        function selectionLabel(items, plural) {
            if (items.length <= 2) return items.map((item) => item.name).join(', ')

            return `${items.length} ${plural}`
        }

        const activeFilters = computed(() => {
            const chips = []

            if (state.filter.clients.length) chips.push({ key: 'clients', label: 'Cliente', value: selectionLabel(state.filter.clients, 'clientes') })
            if (state.filter.status) chips.push({ key: 'status', label: 'Status', value: state.filter.status.name })
            if (state.filter.type) chips.push({ key: 'type', label: 'Tipo', value: state.filter.type.name })
            if (state.filter.vendors.length) chips.push({ key: 'vendors', label: 'Fornecedor', value: selectionLabel(state.filter.vendors, 'fornecedores') })
            if (periodLabel.value) chips.push({ key: 'period', label: 'Período', value: periodLabel.value })

            return chips
        })

        function clearPeriodFilter() {
            stateCalendarFilter.datesInterval = null
            stateCalendarFilter.date.begin = ''
            stateCalendarFilter.date.end = ''
        }

        function clearFilter(key) {

            if (key === 'period') {
                clearPeriodFilter()
            } else if (key === 'clients' || key === 'vendors') {
                state.filter[key] = []
            } else {
                state.filter[key] = null
            }

            loadSummary()
        }

        function clearAllFilters() {
            state.filter.clients = []
            state.filter.status = null
            state.filter.type = null
            state.filter.vendors = []

            filterToday()
        }

        function toNumber(value) {
            return Number(value) || 0
        }

        function formatCurrency(amount) {
            return parseFloat(toNumber(amount)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        }

        function formatPercent(value) {
            return `${toNumber(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
        }

        // eixo Y do gráfico: "R$ 1,2 mil" ocupa menos que o valor cheio
        function formatCompactCurrency(value) {
            return toNumber(value).toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
                notation: 'compact',
                maximumFractionDigits: 1,
            })
        }

        const summaryTotals = computed(() => {

            const summary = state.summary

            const grossAmount = toNumber(summary.depositsAmount) + toNumber(summary.withdrawAmount) + toNumber(summary.removalAmount)
            const totalTax = toNumber(summary.depositsTax) + toNumber(summary.withdrawTax) + toNumber(summary.removalTax)
            const totalCount = toNumber(summary.totalCount)

            return {
                grossAmount,
                totalTax,
                netAmount: toNumber(summary.totalAmount),
                averageTicket: totalCount ? grossAmount / totalCount : 0,
                effectiveRate: grossAmount ? (totalTax / grossAmount) * 100 : 0,
            }
        })

        const summaryCards = computed(() => {

            const summary = state.summary

            const cards = [
                {
                    key: 'deposits',
                    title: 'Depósitos',
                    icon: 'pi-arrow-down',
                    accentColor: 'var(--pill-on-fg)',
                    amount: toNumber(summary.depositsAmount),
                    count: toNumber(summary.depositsCount),
                    totalTax: toNumber(summary.depositsTax),
                    calculatedAmount: toNumber(summary.depositsAmount) - toNumber(summary.depositsTax),
                    highlight: false,
                },
                {
                    key: 'withdraw',
                    title: 'Saques',
                    icon: 'pi-arrow-up',
                    accentColor: 'var(--pill-off-fg)',
                    amount: toNumber(summary.withdrawAmount),
                    count: toNumber(summary.withdrawCount),
                    totalTax: toNumber(summary.withdrawTax),
                    calculatedAmount: toNumber(summary.withdrawAmount) + toNumber(summary.withdrawTax),
                    highlight: false,
                },
                {
                    key: 'removal',
                    title: 'Retiradas',
                    icon: 'pi-arrow-up',
                    accentColor: 'var(--pill-warn-fg)',
                    amount: toNumber(summary.removalAmount),
                    count: toNumber(summary.removalCount),
                    totalTax: toNumber(summary.removalTax),
                    calculatedAmount: toNumber(summary.removalAmount) + toNumber(summary.removalTax),
                    highlight: false,
                },
                {
                    key: 'total',
                    title: 'Total líquido',
                    icon: 'pi-chart-line',
                    // o saldo pode ficar negativo quando saques e taxas superam os depósitos
                    accentColor: toNumber(summary.totalAmount) < 0 ? 'var(--pill-off-fg)' : 'var(--pill-on-fg)',
                    amount: toNumber(summary.totalAmount),
                    count: toNumber(summary.totalCount),
                    totalTax: summaryTotals.value.totalTax,
                    calculatedAmount: toNumber(summary.totalAmount),
                    highlight: true,
                },
            ]

            const maxAmount = Math.max(...cards.map((card) => Math.abs(card.amount)))

            return cards.map((card) => ({
                ...card,
                share: maxAmount ? Math.abs(card.amount) / maxAmount : 0,
            }))
        })

        function countLabel(count) {
            const total = toNumber(count)

            return `${total} ${total === 1 ? 'transação' : 'transações'}`
        }

        function clientCountLabel(count) {
            const total = toNumber(count)

            return `${total} ${total === 1 ? 'cliente' : 'clientes'}`
        }

        // grossAmount é só para ordenar a tabela: a API já devolve as linhas por volume
        const vendorRows = computed(() => {
            return (state.summary.vendors || []).map((vendor) => ({
                ...vendor,
                clients: vendor.clients || [],
                rowKey: `vendor-${vendor.vendorId ?? vendor.vendorName}`,
                grossAmount: toNumber(vendor.depositsAmount) + toNumber(vendor.withdrawAmount) + toNumber(vendor.removalAmount),
            }))
        })

        const vendorTotals = computed(() => {
            const totals = {
                depositsAmount: 0,
                withdrawAmount: 0,
                removalAmount: 0,
                totalTax: 0,
                balance: 0,
            }

            vendorRows.value.forEach((vendor) => {
                totals.depositsAmount += toNumber(vendor.depositsAmount)
                totals.withdrawAmount += toNumber(vendor.withdrawAmount)
                totals.removalAmount += toNumber(vendor.removalAmount)
                totals.totalTax += toNumber(vendor.totalTax)
                totals.balance += toNumber(vendor.balance)
            })

            return totals
        })

        // a API só passou a devolver `vendors` junto com esta tabela; sem o campo, avisa em
        // vez de dizer que o período está vazio
        const vendorEmptyMessage = computed(() => {
            return state.summary.vendors
                ? 'Nenhuma transação no período para separar por fornecedor.'
                : 'A API não retornou a quebra por fornecedor.'
        })

        const timelinePoints = computed(() => state.summary.timeline?.points || [])

        const chartGranularityLabel = computed(() => {
            return state.summary.timeline?.granularity === 'hour' ? 'hora' : 'dia'
        })

        // com o período todo zerado o gráfico seria uma linha reta em zero: melhor o estado vazio
        const hasChartData = computed(() => {
            return timelinePoints.value.some((point) => point.deposits || point.withdraws || point.removals)
        })

        // a API só passou a devolver `timeline` depois do gráfico; sem o campo, avisa em vez
        // de fingir que o período está vazio
        const chartEmptyMessage = computed(() => {
            return state.summary.timeline
                ? 'Nenhuma transação no período para montar o gráfico.'
                : 'A API não retornou a série do período.'
        })

        const chartData = computed(() => {

            const points = timelinePoints.value

            const datasets = [
                {
                    label: 'Depósitos',
                    data: points.map((point) => toNumber(point.deposits)),
                    borderColor: CHART_COLORS.deposits,
                    backgroundColor: CHART_COLORS.depositsFill,
                },
                {
                    label: 'Saques',
                    data: points.map((point) => toNumber(point.withdraws)),
                    borderColor: CHART_COLORS.withdraws,
                    backgroundColor: CHART_COLORS.withdrawsFill,
                },
            ]

            // retiradas são esporádicas: a série só entra quando existe volume
            if (points.some((point) => toNumber(point.removals))) {
                datasets.push({
                    label: 'Retiradas',
                    data: points.map((point) => toNumber(point.removals)),
                    borderColor: CHART_COLORS.removals,
                    backgroundColor: CHART_COLORS.removalsFill,
                })
            }

            return {
                labels: points.map((point) => point.label),
                datasets: datasets.map((dataset) => ({
                    ...dataset,
                    fill: true,
                    tension: 0.35,
                    borderWidth: 2,
                    pointRadius: 0,
                    pointHoverRadius: 4,
                    pointHoverBorderWidth: 2,
                    pointHoverBackgroundColor: '#ffffff',
                })),
            }
        })

        const chartOptions = computed(() => ({
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
                legend: {
                    align: 'end',
                    labels: {
                        usePointStyle: true,
                        boxWidth: 8,
                        color: CHART_COLORS.text,
                        font: { size: 12 },
                    },
                },
                tooltip: {
                    callbacks: {
                        label: (context) => `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`,
                    },
                },
            },
            scales: {
                x: {
                    grid: { display: false },
                    ticks: { color: CHART_COLORS.text, font: { size: 11 }, maxRotation: 0, autoSkipPadding: 12 },
                },
                y: {
                    beginAtZero: true,
                    grid: { color: CHART_COLORS.grid, drawBorder: false },
                    ticks: {
                        color: CHART_COLORS.text,
                        font: { size: 11 },
                        callback: (value) => formatCompactCurrency(value),
                    },
                },
            },
        }))

        const summaryMetrics = computed(() => [
            { label: 'Volume bruto', value: formatCurrency(summaryTotals.value.grossAmount), textColor: '' },
            { label: 'Total de taxas', value: formatCurrency(summaryTotals.value.totalTax), textColor: 'summary-metric-tax' },
            { label: 'Líquido a receber', value: formatCurrency(summaryTotals.value.netAmount), textColor: 'summary-metric-net' },
            { label: 'Ticket médio', value: formatCurrency(summaryTotals.value.averageTicket), textColor: '' },
        ])

        const statusLabelText = computed(() => state.filter.status ? state.filter.status.name : 'todos')

        const summaryText = computed(() => {
            const total = toNumber(state.summary.totalCount)

            return [
                `${total} ${total === 1 ? 'transação' : 'transações'}`,
                `${formatCurrency(summaryTotals.value.grossAmount)} de volume bruto`,
            ].join(' · ')
        })

        return {
            state,
            statusOptions,
            summaryTypesOptions,
            stateClients,
            stateVendors,
            stateCalendarFilter,
            periodLabel,
            activeFilters,
            clearFilter,
            clearAllFilters,
            loadSummary,
            formatCurrency,
            formatPercent,
            summaryTotals,
            summaryCards,
            summaryMetrics,
            chartGranularityLabel,
            hasChartData,
            chartEmptyMessage,
            chartData,
            chartOptions,
            countLabel,
            clientCountLabel,
            vendorRows,
            vendorTotals,
            vendorEmptyMessage,
            statusLabelText,
            summaryText,
        }

    }
}
</script>

<style lang="scss" scoped>
.summary-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
    gap: 1.25rem;
}

.summary-panel-heading {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
}

/* sem align-items os dois cards esticam até a altura da linha, e o flex interno faz o
 * corpo de cada um ocupar a sobra em vez de deixar um vão embaixo */
.summary-panels {
    display: grid;
    grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);
    gap: 1.25rem;
}

.summary-panels > .data-card {
    display: flex;
    flex-direction: column;
}

.summary-chart {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    justify-content: center;
    padding: 1.25rem;
}

/* com maintainAspectRatio: false o canvas segue a altura do container; sem esta regra o
 * .p-chart nasce com altura zero e o gráfico não aparece (o tema não estiliza .p-chart) */
.summary-chart :deep(.p-chart) {
    position: relative;
    height: 280px;
}

.summary-chart :deep(canvas) {
    width: 100% !important;
    height: 100% !important;
}

.summary-panel-body {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 1.25rem;
    padding: 1.25rem;
}

/* a lista de métricas absorve a sobra e a taxa efetiva fica colada no rodapé do card */
.summary-metrics {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
}

.summary-metric-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.75rem 0;
    border-bottom: 1px solid var(--surface-border);
}

.summary-metric-row:first-child {
    padding-top: 0;
}

.summary-metric-row:last-child {
    padding-bottom: 0;
    border-bottom: none;
}

.summary-metric-label {
    font-size: 1rem;
    color: var(--text-color-secondary);
}

.summary-metric-value {
    font-size: 1.2rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: var(--surface-900);
}

.summary-metric-tax {
    color: var(--pill-warn-fg);
}

.summary-metric-net {
    color: var(--pill-on-fg);
}

.summary-effective-rate {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0.35rem;
    padding: 1.25rem;
    border: 1px solid var(--surface-border);
    border-radius: 14px;
    background: var(--surface-50);
}

.summary-effective-rate-label {
    font-size: 0.9rem;
    color: var(--text-color-secondary);
}

.summary-effective-rate-value {
    font-size: 2rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: var(--surface-900);
}

.vendor-name-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.client-breakdown {
    padding: 1rem 1.25rem 1.25rem 4.25rem;
    background: var(--surface-50);
}

.client-breakdown-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.75rem;
}

.client-breakdown-heading > div {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
}

.client-breakdown-table {
    overflow: hidden;
    border: 1px solid var(--surface-border);
    border-radius: 10px;
    background: var(--surface-card);
}

.client-breakdown-empty {
    min-height: 8rem;
}

.vendor-summary-table :deep(.p-datatable-row-expansion > td) {
    padding: 0;
    border-bottom: 1px solid var(--surface-border);
}

.vendor-summary-table :deep(.p-row-toggler) {
    color: var(--primary-color);
}

@media screen and (max-width: 1100px) {
    .summary-panels {
        grid-template-columns: 1fr;
    }

    .client-breakdown {
        padding-left: 1.25rem;
    }
}
</style>
